import { Badge, Button, Drawer, Group, SegmentedControl, Select, Stack, Text } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { useIsAfternoon } from 'src/common/useIsAfternoonHook';
import { useNotificationActions } from 'src/notifications/hooks/useNotificationActions';
import { useNotificationAdvisors } from 'src/notifications/hooks/useNotificationAdvisors';
import { useNotifications } from 'src/notifications/hooks/useNotifications';
import {
  ALL_GROUPS,
  activeNotifications,
  availableGroupLabels,
  type StatusFilter,
  unreadCount,
} from 'src/notifications/notificationFilters';
import { useNotificationDrawerStore } from 'src/notifications/useNotificationDrawerStore';
import AdvisorScopeDropdown from './AdvisorScopeDropdown';
import NotificationList from './NotificationList';

export default function NotificationDrawer({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}) {
  const { data } = useNotifications();
  const { markRead } = useNotificationActions();
  const { isAfternoonAdmin } = useIsAfternoon();
  const queryClient = useQueryClient();
  const filter = useNotificationDrawerStore(state => state.filter);
  const setFilter = useNotificationDrawerStore(state => state.updateFilter);
  const { advisors, advisorOf } = useNotificationAdvisors(opened);
  const notifications = data ?? [];
  const count = unreadCount(notifications);
  const activeItems = activeNotifications(notifications);
  const groupOptions = availableGroupLabels(notifications);

  const typeSelectData = [
    { value: ALL_GROUPS, label: 'All types' },
    ...groupOptions.map(label => ({ value: label, label })),
  ];
  if (filter.group !== ALL_GROUPS && !groupOptions.includes(filter.group)) {
    typeSelectData.push({ value: filter.group, label: filter.group });
  }

  const runRules = useMutation({
    mutationFn: () => api.post('notifications/debug/run-rules'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const markAllRead = () => {
    activeItems
      .filter(notification => notification.status === 'unread')
      .forEach(notification => markRead.mutate(notification.id));
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="md"
      title={
        <Group justify="space-between" wrap="nowrap" w="100%" pr="xs">
          <Group gap="xs" align="center" wrap="nowrap">
            <Text fw={700} size="md">
              Notifications
            </Text>
            {count > 0 && (
              <Badge color="brand" variant="light" size="sm" radius="sm">
                {count} new
              </Badge>
            )}
          </Group>
          <Group gap="xs" wrap="nowrap">
            {isAfternoonAdmin && (
              <Button
                variant="default"
                size="xs"
                onClick={() => runRules.mutate()}
                loading={runRules.isPending}
              >
                Run rules
              </Button>
            )}
            {count > 0 && (
              <Button variant="text" size="xs" onClick={markAllRead}>
                Mark all as read
              </Button>
            )}
          </Group>
        </Group>
      }
      styles={{ title: { flex: 1 } }}
    >
      <Stack gap="sm">
        {activeItems.length > 0 && (
          <Stack gap="xs">
            <Group gap="xs" wrap="nowrap" align="center">
              <SegmentedControl
                size="xs"
                value={filter.status}
                onChange={value =>
                  setFilter(current => ({ ...current, status: value as StatusFilter }))
                }
                data={[
                  { label: 'All', value: 'all' },
                  { label: 'Unread', value: 'unread' },
                ]}
              />
              <Select
                size="xs"
                aria-label="Filter by type"
                value={filter.group}
                onChange={value =>
                  setFilter(current => ({ ...current, group: value ?? ALL_GROUPS }))
                }
                data={typeSelectData}
                allowDeselect={false}
                style={{ flex: 1 }}
              />
            </Group>
            <AdvisorScopeDropdown
              notifications={activeItems}
              advisors={advisors}
              advisorOf={advisorOf}
              value={filter.advisor}
              onChange={advisor => setFilter(current => ({ ...current, advisor }))}
            />
          </Stack>
        )}
        <NotificationList onClose={onClose} filter={filter} advisorOf={advisorOf} />
      </Stack>
    </Drawer>
  );
}
